const toArray = (value) => (Array.isArray(value) ? value : []);

export const ASSIGNMENT_STATUSES = ["PENDING", "IN_PROGRESS", "COMPLETED", "OVERDUE"];

export const normalizeAssignmentStatus = (status) => {
    const normalized = String(status || "").toUpperCase();
    if (ASSIGNMENT_STATUSES.includes(normalized)) return normalized;
    return "PENDING";
};

export const normalizeAssignment = (item = {}) => {
    const student = item.studentId || {};
    const content = item.readingContentId || {};

    return {
        id: item._id || item.id || "",
        student: {
            id: student._id || student.id || "",
            firstName: student.firstName || "",
            lastName: student.lastName || "",
            studentCode: student.studentCode || "",
            grade: student.grade ? String(student.grade) : "",
        },
        readingContent: {
            id: content._id || content.id || "",
            title: content.title || "Untitled",
            level: content.level || "A",
            imageUrl: content.imageUrl || content.image || "no-image.jpg",
        },
        dueDate: item.dueDate || "",
        repetitionCount: Number(item.repetitionCount || 1),
        aiMode: Boolean(item.aiMode),
        status: normalizeAssignmentStatus(item.status),
        createdAt: item.createdAt || "",
    };
};

export const normalizeAssignmentsResponse = (response = {}) => {
    const items = response?.data?.assignments || response?.data || [];
    return toArray(items).map(normalizeAssignment);
};

export const normalizeStudentOption = (student = {}) => {
    const id = student.id || student._id || student.studentId || "";
    const firstName = student.firstName || "";
    const lastName = student.lastName || "";

    return {
        id,
        name: `${firstName} ${lastName}`.trim() || "Student",
        grade: student.grade ? String(student.grade) : "",
        studentCode: student.studentCode || "",
    };
};

export const normalizeContentOption = (content = {}) => {
    return {
        id: content.id || content._id || content.contentId || "",
        title: content.title || "Untitled",
        author: content.author || "ReadWell",
        level: content.level || "A",
        imageUrl: content.imageUrl || content.image || "no-image.jpg",
        categories: Array.isArray(content.categories) ? content.categories : [],
    };
};

export const buildCreateAssignmentPayload = ({
    studentIds,
    readingContentId,
    dueDate,
    repetitionCount,
    aiMode,
}) => {
    const payload = {
        studentIds: toArray(studentIds),
        readingContentId: readingContentId || "",
        repetitionCount: Number(repetitionCount) || 1,
        aiMode: Boolean(aiMode),
    };

    if (dueDate) {
        payload.dueDate = new Date(dueDate).toISOString();
    }

    return payload;
};

export const validateAssignmentDraft = ({ studentIds, readingContentId, dueDate, repetitionCount }) => {
    const errors = {};

    if (!readingContentId) {
        errors.readingContentId = "Please select reading content.";
    }

    if (!toArray(studentIds).length) {
        errors.studentIds = "Please select at least one student.";
    }

    if (!dueDate) {
        errors.dueDate = "Please select a due date.";
    }

    const repetitions = Number(repetitionCount);
    if (!repetitions || repetitions < 1) {
        errors.repetitionCount = "Repetition Count must be at least 1.";
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
    };
};

export const formatAssignmentDate = (value) => {
    if (!value) return "Not set";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Not set";

    return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    }).format(date);
};

export const getAssignmentErrorMessage = (apiError = {}) => {
    const status = apiError?.status || apiError?.response?.status || 0;
    const message = apiError?.message || apiError?.response?.data?.error || "";

    if (status === 403) {
        return "You do not have permission to assign reading content.";
    }

    if (status === 404) {
        return message || "Reading content not found.";
    }

    return message || "Something went wrong. Please try again.";
};
